import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SectionReveal from "@/components/SectionReveal";

const IMG = {
  aerial: "/Images/uganda-tea-field.jpg",
  scientist: "/Images/lab-scientist.jpg",
};

const TIMELINE = [
  ["Stage I", "Foundation", "Regulatory architecture, site selection, and pharmaceutical advisory framework established. UMCC membership."],
  ["Stage II", "Indoor Cultivation Build", "Controlled-environment indoor cultivation infrastructure engineered to GACP and GMP-aligned specifications."],
  ["Stage III", "Post-Harvest Capacity", "GMP-aligned drying, curing, packaging, QA release, and documentation workflows brought online."],
  ["Stage IV", "Export Activation", "Export licensing finalised; certified shipments begin to regulated European importers."],
  ["Stage V", "Scale & Future Extraction", "Capacity expansion, partner-channel integration, and pathway to extraction and product manufacturing."],
];

export default function About() {
  return (
    <>
      <section className="relative pt-[72px] bg-bone">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16">
          <SectionReveal>
            <div className="overline">About Maisha Pharma</div>
          </SectionReveal>
          <SectionReveal delay={120}>
            <h1 className="mt-6 font-display font-light text-4xl md:text-5xl lg:text-[68px] leading-[1.04] tracking-editorial max-w-5xl">
              An East African pharmaceutical platform — engineered for global standards.
            </h1>
          </SectionReveal>
          <SectionReveal delay={240}>
            <p className="mt-8 max-w-2xl text-ink2 text-lg leading-relaxed">
              Maisha Pharma is developing a pharmaceutical-grade medicinal cannabis cultivation and processing platform in Uganda — designed exclusively for regulated international markets.
            </p>
          </SectionReveal>
        </div>

        <SectionReveal>
          <div className="relative h-[380px] md:h-[560px] overflow-hidden">
            <img src={IMG.aerial} alt="Aerial East African landscape" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-forest/20" />
            <div className="absolute bottom-6 left-6 right-6 max-w-[1400px] mx-auto text-bone/90 text-[11px] tracking-[0.22em] uppercase">
              FIG.03 · Origin · East African Plateau
            </div>
          </div>
        </SectionReveal>
      </section>

      <section className="bg-bone">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-16 md:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <SectionReveal>
              <div className="overline">Mandate</div>
              <h2 className="mt-6 font-display font-light text-3xl md:text-4xl lg:text-5xl leading-[1.08] tracking-editorial">
                A focused mandate. A long horizon.
              </h2>
            </SectionReveal>
            <SectionReveal delay={200}>
              <figure className="mt-12 max-w-[240px]">
                <div className="relative overflow-hidden bg-sand">
                  <img src="/Images/Team/Brett.png" alt="Brett Rierson" className="w-full object-cover" loading="lazy" />
                </div>
                <figcaption className="mt-4">
                  <div className="h-px w-10 bg-copper" />
                  <div className="mt-3 text-[11px] tracking-[0.22em] uppercase text-ink">Brett Rierson</div>
                </figcaption>
              </figure>
            </SectionReveal>
          </div>
          <SectionReveal className="lg:col-span-8" delay={140}>
            <p className="text-ink2 text-base leading-relaxed max-w-2xl">
              Led by Brett Rierson — founder of the WFP's Global Post-Harvest Centre — Maisha Pharma applies decades of institutional supply chain rigor to medicinal cannabis.
            </p>
          </SectionReveal>
        </div>

        <SectionReveal>
          <div className="bg-forest text-bone">
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-24">
              <blockquote className="border-l-2 border-copper pl-8 md:pl-12 max-w-4xl">
                <p className="font-display font-light italic text-2xl md:text-3xl lg:text-[34px] leading-snug tracking-editorial">
                  Our facility design is led by an engineering core with a track record of delivering over 15 GMP-certified pharmaceutical infrastructures in frontier markets, ensuring Maisha is compliant by design, not by retrofit.
                </p>
              </blockquote>
            </div>
          </div>
        </SectionReveal>

        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 md:pt-20 pb-24 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <SectionReveal className="lg:col-span-8 lg:col-start-5" delay={120}>
            <p className="text-ink2 text-base leading-relaxed max-w-2xl">
              Maisha Pharma is a member of the Uganda Medicinal Cannabis Consortium (UMCC) and holds a Ugandan cannabis license covering cultivation, processing, domestic distribution, and export.
            </p>
          </SectionReveal>
        </div>
      </section>

      <section className="bg-forest text-bone">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <SectionReveal>
            <div className="text-copper text-[11px] tracking-[0.22em] uppercase font-semibold">Build Timeline</div>
            <h2 className="mt-6 font-display font-light text-3xl md:text-4xl lg:text-5xl leading-[1.08] tracking-editorial max-w-3xl text-bone">
              Phased build — engineered for compliance and scale.
            </h2>
          </SectionReveal>

          <div className="mt-16 space-y-px bg-bone/10 border border-bone/10" data-testid="timeline">
            {TIMELINE.map(([phase, title, body], i) => (
              <SectionReveal key={phase} delay={i * 80}>
                <div className="grid grid-cols-12 gap-6 bg-forest p-6 md:p-10 transition-colors hover:bg-bone/[0.04]">
                  <div className="col-span-12 md:col-span-2 font-mono text-[11px] tracking-[0.22em] uppercase text-copper">{phase}</div>
                  <div className="col-span-12 md:col-span-4 font-display text-xl md:text-2xl tracking-tight text-bone">{title}</div>
                  <div className="col-span-12 md:col-span-6 text-sm md:text-base text-bone/60 leading-relaxed">{body}</div>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-bone">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <SectionReveal className="lg:col-span-6">
            <h2 className="font-display font-light text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-editorial">
              Built quietly, with institutional ambition.
            </h2>
            <p className="mt-6 text-ink2 max-w-lg leading-relaxed">
              Our team brings together pharmaceutical operations, regulated agriculture, and African infrastructure delivery. We are building Maisha Pharma to last decades — not cycles.
            </p>
            <Link
              to="/contact"
              data-testid="about-cta-contact"
              className="mt-10 inline-flex items-center gap-2 bg-forest text-bone px-7 py-4 text-sm tracking-wide hover:bg-forest/85 transition-colors"
            >
              Connect with the team <ArrowUpRight size={16} />
            </Link>
          </SectionReveal>
          <SectionReveal className="lg:col-span-6" delay={140}>
            <div className="relative h-[420px] md:h-[520px] overflow-hidden">
              <img src={IMG.scientist} alt="Pharmaceutical laboratory operations" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-forest/20" />
            </div>
          </SectionReveal>
        </div>
      </section>
    </>
  );
}
